import { GtmConfig } from "./models/gtm-config.model";

export interface CognitoConfig {
  userPoolId: string;
  defaultClientId: string;
  /** Microsoft IDP app client. When absent the OAuth code-exchange flow is disabled. */
  idpClientId?: string;
  /** Base domain, e.g. https://xxx.auth.us-east-1.amazoncognito.com */
  domain: string;
}

export enum EnvId {
  Local = 0,
  Dev = 1,
  Test = 2,
  Prod = 3,
}

export interface Env {
  // From .env.json
  id: EnvId;
  apiUrl: string;
  newApiUrl: string;
  version: string;
  gtmConfig?: Partial<GtmConfig>;
  cognito: CognitoConfig;
  testsBucket: string;
  // Default values
  defaultDateTimeFormat: string;
  defaultDateFormat: string;
  defaultTimezone: string;
  serverDateTimeFormat: string;
  pageLimits: number[];
}

const defaultSettings = {
  version: process.env.REACT_APP_version as string,
  defaultDateTimeFormat: "M/D/YYYY h:mma",
  defaultDateFormat: "M/D/YYYY",
  defaultTimezone: "America/New_York",
  serverDateTimeFormat: "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
  pageLimits: [24, 48, 96],
};

const cognitoDefaults: CognitoConfig = {
  userPoolId: "us-east-1_TuozR9j2Y",
  defaultClientId: "582vpnerq19genvm8072o2dtcn",
  idpClientId: "3oeh3olvbg5vpf8hcsud7002di",
  domain: "https://us-east-1tuozr9j2y.auth.us-east-1.amazoncognito.com",
};

const allEnvSettings = {
  local: {
    PORT: 5000,
    NODE_ENV: "development",
    REACT_APP_id: 0,
    REACT_APP_apiUrl: "https://test-api-5.zapitmedical.com/test/",
    REACT_APP_newApiUrl: "http://localhost:4009/",
    TESTS_BUCKET: "zapit-tests-dev",
    cognito: cognitoDefaults,
  },
  dev: {
    NODE_ENV: "development",
    REACT_APP_id: 1,
    REACT_APP_apiUrl: "https://api-v4.dev.zapitqa.com/",
    REACT_APP_newApiUrl: "https://api.dev.zapitqa.com/",
    TESTS_BUCKET: "zapit-tests-dev",
    cognito: {
      userPoolId: "us-east-1_KsEGXhMKf",
      defaultClientId: "dln0rem9ue0m8u93q7vdt16ch",
      idpClientId: "",
      domain: "https://zapit-auth-dev.auth.us-east-1.amazoncognito.com",
    },
  },
  test: {
    NODE_ENV: "production",
    REACT_APP_id: 2,
    REACT_APP_apiUrl: "https://test-api-5.zapitmedical.com/test/",
    REACT_APP_newApiUrl: "https://test-api.zapitmedical.com/",
    REACT_APP_gtmConfig: '{ "id": "GTM-5F7BHSN", "env": { "gtmAuth": "pnwnejRK9RCxtjEXOuWQLw", "gtmPreview": "env-7" } }',
    TESTS_BUCKET: "zapit-tests-test",
    cognito: cognitoDefaults,
  },
  prod: {
    NODE_ENV: "production",
    REACT_APP_id: 3,
    REACT_APP_apiUrl: "https://api3.zapitmedical.com/prod/",
    REACT_APP_newApiUrl: "https://api.zapitmedical.com/",
    REACT_APP_gtmConfig: '{ "id": "GTM-5F7BHSN" }',
    TESTS_BUCKET: "zapit-tests",
    cognito: cognitoDefaults,
  },
};

function getEnvId(): EnvId {
  // if building locally (or in docker) check if REACT_APP_env is set.
  const npmEnv = process.env.REACT_APP_env;
  if (npmEnv) {
    console.log(`Using ${npmEnv} environment`);
    return envIdFromString(npmEnv.toLocaleLowerCase());
  }

  // if running as static js, check URL for environment
  const host = window.location.hostname.toLocaleLowerCase();
  if (host === "localhost") return EnvId.Local;

  // figure out env from subdomain (app.z.com is prod, app-test.z.com, app-dev.z.com is dev)
  const subdomain = host.split(".")[0].replace("app", "").replace("-", "") || "prod";
  return envIdFromString(subdomain);
}

function envIdFromString(name: string): EnvId {
  if (name === "prod") return EnvId.Prod;
  else if (name === "test") return EnvId.Test;
  else if (name === "dev") return EnvId.Dev;
  else if (name === "local") return EnvId.Local;
  throw new Error("Unable to set config");
}

function buildEnv(): Env {
  const appId = getEnvId();

  const envSettings = (allEnvSettings as any)[EnvId[appId].toLocaleLowerCase()];
  return {
    ...defaultSettings,
    id: appId,
    apiUrl: envSettings.REACT_APP_apiUrl as string,
    newApiUrl: envSettings.REACT_APP_newApiUrl as string,
    gtmConfig: envSettings.REACT_APP_gtmConfig ? JSON.parse(envSettings.REACT_APP_gtmConfig as string) : undefined,
    cognito: envSettings.cognito,
    testsBucket: envSettings.TESTS_BUCKET,
  };
}

const environment: Env = buildEnv();
export default environment;
